import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, RefreshCw, CheckCircle, Shield, Zap } from 'lucide-react';
import SEO from '../../components/SEO';

const ContractToHire = () => {
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: Shield,
      title: 'Risk-Free Trial',
      description: 'Evaluate candidates in real work scenarios before making permanent commitments',
    },
    {
      icon: Zap,
      title: 'Flexible Terms',
      description: 'Convert to permanent employment on your timeline with flexible conversion options',
    },
    {
      icon: CheckCircle,
      title: 'Reduced Hiring Risk',
      description: 'Minimize costly hiring mistakes with extended evaluation periods',
    },
  ];

  const advantages = [
    'Evaluate performance in real work environment',
    'Assess cultural fit over extended period',
    'Flexible conversion timeline based on your needs',
    'No long-term commitment until you\'re ready',
    'Lower initial financial commitment',
    'Easy replacement if candidate doesn\'t fit',
    'Seamless transition to permanent employment',
    'Reduced recruitment costs',
  ];

  const process = [
    {
      phase: 'Contract Phase',
      duration: '3-6 months',
      description: 'Candidate works as contractor while you evaluate performance, skills, and cultural fit',
    },
    {
      phase: 'Evaluation',
      duration: 'Ongoing',
      description: 'Continuous assessment of technical abilities, work quality, and team integration',
    },
    {
      phase: 'Conversion',
      duration: 'Flexible',
      description: 'Convert to permanent employment when both parties are ready and satisfied',
    },
  ];

  const idealFor = [
    {
      title: 'Growing Startups',
      description: 'Test team expansion without immediate long-term financial commitments',
    },
    {
      title: 'Uncertain Projects',
      description: 'Bring on specialized skills for projects with evolving requirements',
    },
    {
      title: 'Team Expansion',
      description: 'Scale your team carefully while ensuring the right fit',
    },
    {
      title: 'Specialized Roles',
      description: 'Find niche expertise and evaluate before making it permanent',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contract-to-Hire IT Staffing"
        description="Test-fit IT professionals before making permanent commitments. Flexible contract-to-hire solutions perfect for growing teams. Reduce hiring risks with extended evaluation periods."
        keywords="contract to hire, temp to perm, try before hire, IT contractors, flexible hiring, contract developers, trial period employment"
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-yellow-50 to-orange-50 opacity-60"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-orange-400/20 via-yellow-400/20 to-orange-400/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block mb-6 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-slate-200">
              <span className="text-sm font-medium text-slate-700">Contract-to-Hire</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Try before you{' '}
              <span className="bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">
                commit
              </span>
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Test-fit IT professionals before making permanent commitments. Perfect for dynamic teams and growing organizations that want to reduce hiring risks.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold text-lg group"
              >
                <span>Get Started</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <a
                href="tel:+91-9121711433"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-white text-slate-900 rounded-full border-2 border-slate-200 hover:border-slate-300 transition-all duration-200 font-semibold text-lg"
              >
                <span>Call: +91-9121711433</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        ref={(el) => (sectionsRef.current[0] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              The best of both worlds
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Combine the flexibility of contracting with the stability of permanent employment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-50 rounded-2xl p-8 hover:bg-slate-100 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section
        ref={(el) => (sectionsRef.current[1] = el)}
        className="py-24 px-6 bg-slate-50 opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              How it works
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              A smooth transition from contract to permanent employment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
                <div className="text-sm font-semibold text-[#f9a825] mb-2">{item.duration}</div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.phase}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section
        ref={(el) => (sectionsRef.current[2] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Why choose contract-to-hire?
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Make smarter hiring decisions by evaluating candidates in real work scenarios before committing to permanent employment.
              </p>
              <ul className="space-y-3">
                {advantages.map((advantage, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#f9a825] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{advantage}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-yellow-500 p-12 rounded-3xl shadow-2xl">
              <RefreshCw className="w-32 h-32 text-white mx-auto opacity-30" />
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section
        ref={(el) => (sectionsRef.current[3] = el)}
        className="py-24 px-6 bg-slate-50 opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Perfect for
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Contract-to-hire is ideal for organizations in these situations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {idealFor.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={(el) => (sectionsRef.current[4] = el)}
        className="py-24 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-0 transition-opacity duration-1000 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-yellow-500/10"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to try before you commit?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Let's discuss how contract-to-hire can reduce your hiring risks
          </p>
          <Link
            to="/contact-us"
            className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold text-lg group"
          >
            <span>Contact Us Today</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ContractToHire;
